import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeDollarSign, PenTool, PackageCheck, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { FadeUp, MaskedLine } from "../components/motion";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import RichText from "../components/RichText";

const VALUE_ICONS = [BadgeDollarSign, PenTool, PackageCheck];
const IMG = "https://images.unsplash.com/photo-1732257297170-09599f1bbaec?q=80&w=1200&auto=format&fit=crop";

export default function About() {
  const { t, ts, lang, site } = useLang();
  const values = t("about.values").map((v, i) => ({ ...v, icon: VALUE_ICONS[i] }));
  const services = t("about.services");
  const [aiText, setAiText] = useState(null);

  useEffect(() => {
    // Texte « Rédiger par l'IA » publié depuis l'admin (remplace le texte par défaut)
    api.get("/settings/shop").then((r) => {
      const ap = r.data?.about_page;
      if (ap?.fr || ap?.en) {
        setAiText(lang === "en" ? (ap.en || ap.fr) : (ap.fr || ap.en));
      }
    }).catch(() => {});
  }, [lang]);

  const paragraphs = aiText ? aiText.split(/\n\s*\n/).filter(Boolean) : null;

  return (
    <div data-testid="about-page" className="pt-32 pb-28">
      <SEO title={ts("seo.about_title")} description={ts("seo.about_desc")} path="/a-propos"
        jsonLd={{ "@context": "https://schema.org", "@type": "AboutPage",
                  name: ts("seo.about_title"), url: `https://mrtrophee.ca/a-propos`,
                  inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
                  isPartOf: { "@type": "WebSite", url: "https://mrtrophee.ca" },
                  about: { "@id": "https://mrtrophee.ca/#entreprise" } }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("about.kicker")}</p>
          <h1 className="font-serif text-5xl md:text-7xl leading-tight mb-16">
            <MaskedLine key={ts("about.title_1")} delay={0.15}>{t("about.title_1")}</MaskedLine>
            <MaskedLine key={ts("about.title_2")} delay={0.35} className="italic text-gold">{t("about.title_2")}</MaskedLine>
          </h1>
        </FadeUp>

        <div className="grid md:grid-cols-2 gap-14 items-center mb-28">
          <FadeUp>
            {paragraphs ? (
              <div data-testid="about-ai-text">
                {paragraphs.map((p, i) => (
                  <p key={i} className={`leading-relaxed mb-6 ${i === 0 ? "text-ivory/70 text-base md:text-lg" : "text-ivory/60"}`}>{p}</p>
                ))}
              </div>
            ) : (
              <>
                <p className="text-ivory/70 text-base md:text-lg leading-relaxed mb-6">{t("about.p1")}</p>
                <p className="text-ivory/60 leading-relaxed">{t("about.p2")}</p>
              </>
            )}
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="card-clip overflow-hidden">
              <motion.img
                src={site("img.about") || IMG} alt="Atelier de gravure"
                data-edit-image="img.about"
                className="w-full h-[380px] object-cover"
                whileHover={{ scale: 1.05 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </FadeUp>
        </div>

        <div className="grid sm:grid-cols-3 gap-8 mb-24">
          {values.map((v, i) => (
            <FadeUp key={v.title} delay={i * 0.1}>
              <div className="bg-surface border border-gold/15 rounded-xl p-8 h-full hover:border-gold/40 transition-colors duration-500">
                <v.icon className="w-7 h-7 text-gold mb-5" />
                <h3 className="font-serif text-2xl mb-3"><RichText text={v.title} path={`about.values.${i}.title`} /></h3>
                <p className="text-ivory/60 text-sm leading-relaxed"><RichText text={v.text} path={`about.values.${i}.text`} /></p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mb-12">
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("about.services_kicker")}</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-5">
            {t("about.services_title_1")} <em className="italic text-gold">{t("about.services_title_2")}</em>
          </h2>
          <p className="text-ivory/60 max-w-2xl">{t("about.services_sub")}</p>
        </FadeUp>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-24" data-testid="about-services">
          {services.map((s, i) => (
            <FadeUp key={s} delay={Math.min(i * 0.04, 0.5)}>
              <div className="flex items-start gap-3 bg-surface border border-ivory/10 rounded-lg px-5 py-4 h-full hover:border-gold/40 transition-colors duration-500">
                <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                <p className="text-sm text-ivory/70 leading-snug"><RichText text={s} path={`about.services.${i}`} /></p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="text-center">
          <Link
            to="/boutique" data-testid="about-cta"
            className="group inline-flex items-center gap-3 bg-gold text-ink font-semibold px-10 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
          >
            {t("about.cta")}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </div>
  );
}
